import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../../config";
import DownloadButtons from "../DownloadButtons/DownloadButtons";
import EntriesSelector from "../Pagination/EntriesSelector";
import Pagination from "../Pagination/Paginations.js";
import Select from 'react-select';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const Attendance_summary = () => {
  const today = new Date().toISOString().split("T")[0];
  const [formData, setFormData] = useState({
    from_date: today,
    to_date: today,
    dept_id: "",
  });

  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [report, setReport] = useState([]);
  const [dates, setDates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  // 🔹 Fetch departments
  const handleDepartment = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/showdepartment`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      setDepartments(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load departments");
    }
  };

  useEffect(() => {
    handleDepartment();
  }, []);

  // 🔹 Handle input change
  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  // 🔹 Fetch attendance summary
  const handleSubmit = async (e) => {
    e.preventDefault();

    // block repeat clicks while a request is already running
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/attendancesummary`,
        {
          from_date: formData.from_date,
          to_date: formData.to_date,
          dept_id: formData.dept_id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      setReport(Object.values(res.data.records || {}));
      setDates(res.data.dates || []);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      setReport([]);
      setDates([]);
      toast.error("Failed to fetch attendance summary", { toastId: "attendance-summary-error" });
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  // 🔹 Date column header — same format as the CI report (d-m-Y)
  const dayLabel = (date) => {
    const [y, m, d] = date.split("-");
    return `${d}-${m}-${y}`;
  };

  // 🔹 Flatten data for CSV/PDF
const generateFlatData = () => {
  return report.map((emp) => {
    const flatRow = {
      "Employee ID": emp.employee_id,
      "Employee Name": emp.employee_name,
      "Department": emp.department,
    };

    dates.forEach((date) => {
      const att = emp.attendance?.[date] || {};
      const label = dayLabel(date);

      flatRow[`${label} - In`] = att.clock_in ?? "-";
      flatRow[`${label} - Out`] = att.clock_out ?? "-";
      flatRow[`${label} - TW`] = att.work_hour ?? "-";
    });

    return flatRow;
  });
};


  // 🔹 Search filter (by ID, name, or department)
  const filteredRecords = report.filter(
    (emp) =>
      emp.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.employee_id?.toString().includes(searchTerm)
  );

  // 🔹 Pagination logic
  const totalPages = Math.ceil(filteredRecords.length / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;

  const currentRecords = filteredRecords.slice(indexOfFirst, indexOfLast);

  return (
    <div className="container mt-4">
     <ToastContainer position="bottom-right" />
      <div className="card shadow">
        <div className="card-header">
          <h3 className="card-title d-flex align-items-center mb-0" style={{ color: "#1d3a8a", fontWeight: 600 }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="#1d3a8a" style={{ marginRight: "8px", verticalAlign: "-3px" }}>
              <path fillRule="evenodd" d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m1.679-4.493-1.335 2.226a.75.75 0 0 1-1.174.144l-.774-.773a.5.5 0 0 1 .708-.708l.547.548 1.017-1.694a.5.5 0 1 1 .858.257z"/>
              <path d="M11 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0M8 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4"/>
              <path d="M8.256 14a4.5 4.5 0 0 1-.229-1.004H3c.001-.246.154-.986.832-1.664C4.484 10.68 5.711 10 8 10q.39 0 .74.025c.226-.341.496-.65.804-.918Q8.844 9.002 8 9c-5 0-6 3-6 4s1 1 1 1z"/>
            </svg>
            Attendance Summary Report
          </h3>
        </div>

        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3">
          <div className="col-md-3">
  <label>From Date</label>

  <DatePicker
    selected={formData.from_date ? new Date(formData.from_date) : null}
    onChange={(date) => {
      const value = date
        ? date.toLocaleDateString("en-CA")
        : "";

      setFormData((prev) => ({
        ...prev,
        from_date: value,
      }));
    }}
    dateFormat="yyyy-MM-dd"
    placeholderText="Select From Date"
    className="form-control"
  />
</div>

<div className="col-md-3">
  <label>To Date</label>
  <DatePicker
    selected={formData.to_date ? new Date(formData.to_date) : null}
    onChange={(date) => {
      const value = date
        ? date.toLocaleDateString("en-CA")
        : "";

      setFormData((prev) => ({
        ...prev,
        to_date: value,
      }));
    }}
    dateFormat="yyyy-MM-dd"
    placeholderText="Select From Date"
    className="form-control"
  />
</div>


            <div className="col-md-3">
              <label>Department</label>
                 <Select
    name="dept_id"
    value={departments
      .map((dept) => ({
        value: dept.department_id,
        label: dept.department_name,
      }))
      .find((d) => d.value === formData.dept_id) || null}
    onChange={(selected) =>
      handleChange({
        target: { name: "dept_id", value: selected ? selected.value : "" },
      })
    }
    options={departments.map((dept) => ({
      value: dept.department_id,
      label: dept.department_name,
    }))}
    isClearable
    placeholder="All"
    className="basic-single"
  />
            </div>
        <div className="col-md-3 d-flex align-items-end">
  <button className="btn btn-primary btn-sm" style={{ height: "38px" }} type="submit" disabled={loading}>
    {loading ? "Loading..." : "Get"}
  </button>
</div>


          </form>
        </div>
      </div>

      {/* 🔹 Controls Section — only visible when data exists */}
      {report.length > 0 && (
        <div
          className="d-flex flex-wrap align-items-center justify-content-between my-3"
          style={{ gap: "1rem" }}
        >
          <div className="d-flex align-items-center gap-3">
            <DownloadButtons
              data={generateFlatData()}
              filename="attendance_summary_report"
            />
          </div>

          {/* ✅ Show entries + Search box floats right */}
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <EntriesSelector
              entriesPerPage={entriesPerPage}
              setEntriesPerPage={setEntriesPerPage}
              setCurrentPage={setCurrentPage}
            />
            <div style={{ width: "250px" }}>
              <input
                type="text"
                placeholder="Search employee or dept..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="form-control"
              />
            </div>
          </div>
        </div>
      )}

      {/* 🔹 Table Section */}
      <div className="card mt-4">
        <div className="card-body table-responsive sticky-table-wrap">
          {loading && <p className="text-center text-info">Loading...</p>}

          {!loading && report.length > 0 ? (
            <table className="table table-bordered text-center align-middle sticky-table sticky-head-tint sticky-table-2row sticky-no-freeze-col">
              <thead>
                {/* Top Row – Dates */}
                <tr
                 
                >
                  <th rowSpan="2">Employee Id</th>
                  <th rowSpan="2">Employee</th>
                  <th rowSpan="2">Department</th>
                  {dates.map((d, i) => (
                    <th key={i} colSpan="3">
                      {dayLabel(d)}
                    </th>
                  ))}
                </tr>

                {/* Sub-row – Column labels */}
                <tr
                 
                >
                  {dates.map((_, i) => (
                    <React.Fragment key={i}>
                      <th>In</th>
                      <th>Out</th>
                      <th>TW</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>

              <tbody>
                {currentRecords.map((emp, i) => (
                  <tr key={emp.employee_id ?? i}>
                    <td>{emp.employee_id}</td>
                    <td>{emp.employee_name}</td>
                    <td>{emp.department}</td>

                    {dates.map((date, j) => {
                      const att = emp.attendance?.[date] || {};

                      // green highlight for a manually updated punch
                      const style = att.manual
                        ? { backgroundColor: "#198754", color: "#fff" }
                        : att.before_doj
                        ? { color: "#999" } // before date of joining
                        : {};

                      return (
                        <React.Fragment key={j}>
                          <td style={style}>{att.clock_in ?? "-"}</td>
                          <td style={style}>{att.clock_out ?? "-"}</td>
                          <td style={style}>{att.work_hour ?? "-"}</td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            !loading && (
              <p className="text-center text-muted">No records found</p>
            )
          )}
        </div>
      </div>
       <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
    </div>
  );
};

export default Attendance_summary;
